import {useCallback, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import useHandleCloseModal from '../../../hooks/HandleCloseModal'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import Swal from 'sweetalert2'
import {KTSVG} from '../../../_metronic/helpers'
import {IComponent, ISection} from '../../../interfaces/Analytics/Analytics'
import clsx from 'clsx'
import {currencyFormatter, customNumberFormat, handleCaps} from '../../../functions/general'
import useAccessToken from '../../../hooks/AccessToken'
import Select from 'react-select'
import {DateRangePickerProps} from 'rsuite/esm/DateRangePicker'
import {ColumnDef, createColumnHelper} from '@tanstack/react-table'
import {IconButton, Menu, MenuItem} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import useModalEffect from '../../../hooks/useModalEffect'
import AddCore from './AddCore'
import EditCore from './EditCore'
import {inserComponent, previewComponent} from '../../../api/Analitik/ComponentCRUD'
import DateRangePickerInterval from './DateRangePickerInterval'
import {useAnalyticsStore} from '../../../stores/Analytics/AnalyticStore'
import TableLite from '../../DataTable/TableLite'
import moment from 'moment-timezone'
import ComponentAnalytics from './ComponentAnalytics'
import { mutate } from 'swr'

interface IProps {
  section: ISection
}
interface IRule {
  typeFilter: string
  valueFilter: any[] | string
}
interface RowData {
  index: number
  name: string
  tableName: string
  coreType: string
  colorCode: string
  relationLead: boolean
  rules: IRule[]
}

const columnHelper = createColumnHelper<RowData>()

export default function AddComponent({section}: IProps) {
  const intl = useIntl()
  const modalRef = useRef<HTMLDivElement | null>(null)
  const closeModalRef = useRef<HTMLButtonElement>(null)
  const [modalBtnLoading, setModalBtnLoading] = useState(false)
  const token = useAccessToken()
  const {components, setComponents} = useAnalyticsStore()
  const [previewComponentData, setPreviewComponentData] = useState<IComponent | undefined>()
  const [previewComponentLoading, setPreviewComponentLoading] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const [isPreviewShowed, setIsPreviewShowed] = useState<boolean>(false)
  const [showAddCoreModal, setShowAddCoreModal] = useState(false)
  const [showEditCoreModal, setShowEditCoreModal] = useState(false)
  const [showPreviewmComponentAnalytic, setShowPreviewmComponentAnalytic] = useState(false)
  const [isCopyCase, setIsCopyCase] = useState(false)

  const [indexEditCore, setIndexCoreModal] = useState()

  const [stepForm, setStepForm] = useState('1')
  const handleClose = () => {
    setAnchorEl(null)
    setOpenMenuId(null)
  }

  const columns: ColumnDef<RowData, any>[] = [
    {
      id: 'actions',
      header: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.ACTION'}),
      size: 25,
      cell: (info: any) => {
        const row = info.row.original
        const rowIndex = info.row.index

        return (
          <>
            <IconButton
              aria-label={`more-${rowIndex}`}
              onClick={(event) => {
                setAnchorEl(event.currentTarget)
                setOpenMenuId(rowIndex)
              }}
            >
              <MoreVertIcon />
            </IconButton>

            <Menu
              id={`menu-${rowIndex}`}
              open={openMenuId === rowIndex}
              anchorEl={anchorEl}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() => {
                  handleClose()
                  setShowEditCoreModal(true)
                  setIndexCoreModal(rowIndex)
                }}
              >
                <i className='fas fa-edit fs-4 me-4'></i>
                <span>{intl.formatMessage({id: 'FORM.LABEL.UPDATE'})}</span>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  formik.setFieldValue(
                    'dataCore',
                    formik.values.dataCore.filter((_, index) => index !== rowIndex)
                  )
                }}
              >
                <i className='fas fa-trash fs-3 me-5'></i>
                <span>{intl.formatMessage({id: 'FORM.LABEL.DELETE'})}</span>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose()
                  setShowEditCoreModal(true)
                  setIndexCoreModal(rowIndex)
                  setIsCopyCase(true)
                }}
              >
                <i className='bi bi-clipboard fs-3 me-5'></i>
                <span>{intl.formatMessage({id: 'FORM.LABEL.COPY'})}</span>
              </MenuItem>
            </Menu>
          </>
        )
      },
    },
    columnHelper.accessor('name', {
      header: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.NAME'}),
      size: 30,
      cell: (info) => (
        <span className='d-flex justify-content-between align-items-center gap-3'>
          <span>{info.getValue()}</span>
        </span>
      ),
    }),
    columnHelper.accessor('coreType', {
      header: intl.formatMessage({id: 'FORM.LABEL.DATA_STATUS'}),
      size: 30,
      cell: (info) => {
        let dataStatus = ''
        if (info.getValue() === 'created') {
          dataStatus = intl.formatMessage({id: 'FORM.OPTION.LABEL.CREATED'})
        }
        if (info.getValue() === 'finished') {
          dataStatus = intl.formatMessage({id: 'FORM.OPTION.LABEL.FINISHED'})
        }

        return (
          <span className='d-flex justify-content-between align-items-center gap-3'>
            <span>{dataStatus}</span>
          </span>
        )
      },
    }),
    columnHelper.accessor('colorCode', {
      header: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.COLOR'}),
      size: 30,
      cell: (info) => (
        <span className='d-flex justify-content-between align-items-center gap-3'>
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '6px',
              backgroundColor: info.getValue() ?? '#D33115',
            }}
          />
        </span>
      ),
    }),
    columnHelper.accessor('relationLead', {
      header: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.RELATION_LEAD'}),
      size: 30,
      cell: (info) => {
        const tableName = info.row.original.tableName
        return (
          <span className='d-flex justify-content-between align-items-center gap-3'>
            <span>
              {tableName === 'PIPELINE'
                ? '-'
                : info.getValue()
                ? intl.formatMessage({id: 'YES'})
                : intl.formatMessage({id: 'NOT'})}
            </span>
          </span>
        )
      },
    }),
  ]

  const timeFrameOptions = [
    {value: 'DAILY', label: intl.formatMessage({id: 'FORM.LABEL.DAILY'})},
    {value: 'WEEKLY', label: intl.formatMessage({id: 'FORM.LABEL.WEEKLY'})},
    {value: 'MONTHLY', label: intl.formatMessage({id: 'FORM.LABEL.MONTHLY'})},
  ]

  const typeChartOptions = [
    {label: 'Bar', value: 'BAR'},
    {label: 'Pie', value: 'PIE'},
    {label: 'Widget', value: 'SUMMARY'},
    {label: 'Table', value: 'LIST'},
  ]
  const dataTypeOption = [
    {value: 'CALCULATION', label: intl.formatMessage({id: 'FORM.OPTION.TYPE.CALCULATION'})},
    {value: 'NOMINAL', label: intl.formatMessage({id: 'FORM.OPTION.TYPE.NOMINAL'})},
    {value: 'AGE', label: intl.formatMessage({id: 'FORM.OPTION.TYPE.AGE'})},
  ]
  const API_URL = process.env.REACT_APP_API_URL


  const formik = useFormik({
    initialValues: {
      sectionId: section.sectionId,
      name: '',
      dataType: '',
      timeFrame: '',
      chartType: '',
      isRealTime: false,
      totalData: 0,
      interval: null,
      dataCore: [],
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required(
        intl.formatMessage(
          {id: 'FORM.VALIDATION.FIELD_REQUIRED'},
          {title: intl.formatMessage({id: 'FORM.LABEL.NAME'})}
        )
      ),
      dataType: Yup.string().required(
        intl.formatMessage({id: 'FORM.VALIDATION.FIELD_REQUIRED'}, {title: 'Tipe Data'})
      ),
      chartType: Yup.string().required(
        intl.formatMessage(
          {id: 'FORM.VALIDATION.FIELD_REQUIRED'},
          {title: intl.formatMessage({id: 'FORM.LABEL.TYPE_CHART'})}
        )
      ),
      timeFrame: Yup.string().required(
        intl.formatMessage(
          {id: 'FORM.VALIDATION.FIELD_REQUIRED'},
          {title: intl.formatMessage({id: 'FORM.LABEL.TIME_FRAME'})}
        )
      ),
      totalData: Yup.number().when('isRealTime', {
        is: true,
        then: Yup.number()
          .positive(intl.formatMessage({id: 'FORM.VALIDATION.POSITIVE_VALUE'}))
          .max(100, intl.formatMessage({id: 'FORM.VALIDATION.MAX_VALUE'}, {title: 100}))
          .min(1, intl.formatMessage({id: 'FORM.VALIDATION.MIN_VALUE'}, {title: 1}))
          .required(
            intl.formatMessage(
              {id: 'FORM.VALIDATION.FIELD_REQUIRED'},
              {title: intl.formatMessage({id: 'FORM.LABEL.TOTAL_DATA'})}
            )
          ),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
      interval: Yup.array()
        .nullable()
        .when('isRealTime', {
          is: false,
          then: (schema) =>
            schema
              .required(
                intl.formatMessage(
                  {id: 'FORM.VALIDATION.FIELD_REQUIRED'},
                  {title: intl.formatMessage({id: 'FORM.LABEL.INTERVAL'})}
                )
              )
              .min(1, 'Interval tidak boleh kosong'),
          otherwise: (schema) => schema.notRequired(),
        }),

      dataCore: Yup.array()
        .min(
          1,
          intl.formatMessage(
            {id: 'FORM.VALIDATION.FIELD_REQUIRED'},
            {title: intl.formatMessage({id: 'FORM.LABEL.DATA_CORE'})}
          )
        ) // Validasi agar dataCore memiliki lebih dari 0 elemen
        .required(
          intl.formatMessage(
            {id: 'FORM.VALIDATION.FIELD_REQUIRED'},
            {title: intl.formatMessage({id: 'FORM.LABEL.DATA_CORE'})}
          )
        ),
    }),
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setSubmitting(true)
      setModalBtnLoading(true)
      try {
        const [startDate, endDate] = Array.isArray(values.interval)
          ? [
              moment(values.interval[0] ?? new Date()).format('YYYY-MM-DD'),
              moment(values.interval[1] ?? new Date()).format('YYYY-MM-DD'),
            ]
          : [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]

        const response = await inserComponent({
          chartType: values.chartType,
          name: values.name,
          dataType: values.dataType,
          timeFrame: values.timeFrame,
          dataCore: values.dataCore,
          endDate,
          sectionId: section.sectionId,
          token: token,
          startDate,
          totalData: values.totalData,
          isRealTime: values.isRealTime,
        })
        if (response.data.success) {
          Swal.fire({
            icon: 'success',
            heightAuto: false,
            title: intl.formatMessage({id: 'FORM.VALIDATION.ADD_SUCCESS'}),
            timer: 2000,
            timerProgressBar: true,
          })
        }
        setComponents([...components, response.data.data[0]])
        closeModalRef.current?.click()
      } catch (error: any) {
        Swal.fire({
          icon: 'error',
          title: error.response.data.message,
          confirmButtonText: 'Ok',
        })
      } finally {
        setSubmitting(false)
        setModalBtnLoading(false)
        mutate(`${API_URL}/menu-analytics/limit`)
      }
    },
  })
  const {handleCloseModal} = useHandleCloseModal(modalRef, closeModalRef, formik)

  const handleLoadPreview = async () => {
    const errors = await formik.validateForm() // Validasi form dulu
    if (Object.keys(errors).length > 0) {
      return
    }
    setPreviewComponentLoading(true)
    setIsPreviewShowed(true)

    try {
      const [startDate, endDate] = formik.values.isRealTime
            ? ['', '']
            : [
                moment(formik.values.interval?.[0]).format('YYYY-MM-DD'),
                moment(formik.values.interval?.[1]).format('YYYY-MM-DD'),
              ]

      const response = await previewComponent({
        chartType: formik.values.chartType,
        name: formik.values.name,
        dataType: formik.values.dataType,
        timeFrame: formik.values.timeFrame,
        dataCore: formik.values.dataCore,
        sectionId: formik.values.sectionId,
        totalData: formik.values.totalData,
        isRealTime: formik.values.isRealTime,
        token: token,
        endDate: !formik.values.isRealTime ? endDate : '',
        startDate: !formik.values.isRealTime ? startDate : '',
      })
      setPreviewComponentData(response.data.data[0])
    } catch (error) {
      console.error('Error loading preview:', error)
    } finally {
      setPreviewComponentLoading(false)
    }
  }

  useEffect(() => {
    const modalElement = modalRef.current

    const handleModalHidden = () => {
      formik.resetForm()
    }

    modalElement?.addEventListener('hidden.bs.modal', handleModalHidden)

    return () => {
      modalElement?.removeEventListener('hidden.bs.modal', handleModalHidden)
    }
  }, [formik])

  const handleChangeInterval: DateRangePickerProps['onChange'] = (value) => {
    formik.setFieldValue('interval', value)
  }
  const getTypeTotalData = useCallback(() => {
    const type = formik.values.timeFrame
    switch (type) {
      case 'DAILY':
        return intl.formatMessage({id: 'CALENDAR.LABEL.DAY'})
      case 'MONTHLY':
        return intl.formatMessage({id: 'CALENDAR.LABEL.MONTH'})
      case 'WEEKLY':
        return intl.formatMessage({id: 'CALENDAR.LABEL.WEEK'})
      default:
        return "Data"
       
    }
  }, [formik.values.timeFrame])
  useModalEffect({
    showModal: showAddCoreModal,
    setShowModal: setShowAddCoreModal,
    modalId: `add-core-modal`,
  })
  useModalEffect({
    showModal: showEditCoreModal,
    setShowModal: setShowEditCoreModal,
    modalId: `edit-core-modal-${indexEditCore}`,
  })


  return (
    <>
      <div className={`modal fade`} tabIndex={-1} id={`add-component-modal`} ref={modalRef}>
        <div className='modal-dialog modal-dialog-centered modal-lg'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>{intl.formatMessage({id: 'FORM.ACTION.ADD'})}</h5>
              <button
                className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                onClick={handleCloseModal}
                ref={closeModalRef}
              >
                <KTSVG
                  path='/media/icons/duotune/arrows/arr061.svg'
                  className='svg-icon svg-icon-2x'
                />
              </button>
            </div>
            {/* form */}

            <form onSubmit={formik.handleSubmit} noValidate>
              <div
                className={clsx('modal-body container')}
              >
                <div className=' row d-flex justify-content-center align-items-center '>
                  <div className={clsx('col-12')}>
                    {/* name */}
                    <>
                      <label className='form-label fs-6 fw-bolder text-dark required'>
                        {intl.formatMessage({id: 'FORM.LABEL.NAME'})}
                      </label>
                      <div className='input-group mb-3'>
                        <input
                          placeholder={intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.NAME'})}
                          {...formik.getFieldProps('name')}
                          className={clsx('form-control form-control-lg', {
                            'is-invalid': formik.touched.name && formik.errors.name,
                          })}
                          type='text'
                          name='name'
                          autoComplete='off'
                          onInput={(e) => handleCaps(e)}
                        />
                      </div>
                      {formik.touched.name && formik.errors.name && (
                        <div className='fv-plugins-message-container text-danger'>
                          <span role='alert' className='text-danger'>
                            {formik.errors.name}
                          </span>
                        </div>
                      )}
                    </>
                    {/* jenis chart */}
                    <>
                      <label className='form-label fs-6 fw-bolder text-dark required'>
                        {intl.formatMessage({id: 'FORM.LABEL.CHART_TYPE'})}
                      </label>
                      <div className='input-group mb-3'>
                        <Select
                          closeMenuOnSelect={true}
                          openMenuOnClick
                          options={typeChartOptions}
                          placeholder={`${intl.formatMessage({
                            id: 'FORM.ACTION.CHOOSE',
                          })} ${intl.formatMessage({id: 'FORM.LABEL.CHART_TYPE'})}`}
                          value={typeChartOptions.filter(
                            (option: any) => formik.values.chartType === option.value
                          )}
                          onChange={(selectedOptions) => {
                            const selectedValues = selectedOptions
                            if (
                              (selectedOptions?.value === 'PIE'||selectedOptions?.value === 'SUMMARY') &&
                              formik.values.dataCore.length > 1
                            ) {
                              formik.setFieldValue('dataCore', [])
                            }
                            formik.setFieldValue('chartType', selectedValues?.value)
                          }}
                          className='w-100 '
                          onBlur={() =>
                            formik.values.chartType === '' &&
                            formik.setFieldTouched('chartType', true)
                          }
                          styles={{
                            menu: (provided) => ({...provided, zIndex: 9999}),
                          }}
                        />
                      </div>
                      {formik.touched.chartType && formik.errors.chartType && (
                        <div className='fv-plugins-message-container text-danger'>
                          <span role='alert' className='text-danger'>
                            {formik.errors.chartType}
                          </span>
                        </div>
                      )}
                    </>
                    {/* jenis data */}
                    <>
                      <label className='form-label fs-6 fw-bolder text-dark required'>
                        {intl.formatMessage({id:"FORM.LABEL.DATA_TYPE"})}
                      </label>
                      <div className='input-group mb-3'>
                        <Select
                          closeMenuOnSelect={true}
                          openMenuOnClick
                          options={dataTypeOption}
                          placeholder={`${intl.formatMessage({
                            id: 'FORM.ACTION.CHOOSE',
                          })}  ${intl.formatMessage({id:"FORM.LABEL.DATA_TYPE"})}`}
                          value={dataTypeOption.filter(
                            (option: any) => formik.values.dataType === option.value
                          )}
                          onChange={(selectedOptions) => {
                            const selectedValues = selectedOptions
                            formik.setFieldValue('dataType', selectedValues?.value)
                          }}
                          className='w-100 '
                          onBlur={() =>
                            formik.values.dataType === '' &&
                            formik.setFieldTouched('dataType', true)
                          }
                          styles={{
                            menu: (provided) => ({...provided, zIndex: 9999}),
                          }}
                        />
                      </div>
                      {formik.touched.dataType && formik.errors.dataType && (
                        <div className='fv-plugins-message-container text-danger'>
                          <span role='alert' className='text-danger'>
                            {formik.errors.dataType}
                          </span>
                        </div>
                      )}
                    </>
                    {/* timeframe */}

                    <>
                      <label className='form-label fs-6 fw-bolder text-dark required'>
                        {intl.formatMessage({id: 'FORM.LABEL.TIME_FRAME'})}
                      </label>
                      <div className='input-group mb-3'>
                        <Select
                          closeMenuOnSelect={true}
                          openMenuOnClick
                          options={timeFrameOptions}
                          placeholder={`${intl.formatMessage({
                            id: 'FORM.ACTION.CHOOSE',
                          })} ${intl.formatMessage({id: 'FORM.LABEL.TIME_FRAME'})}`}
                          value={timeFrameOptions.filter(
                            (option: any) => formik.values.timeFrame === option.value
                          )}
                          onChange={(selectedOptions) => {
                            const selectedValues = selectedOptions
                            formik.setFieldValue('timeFrame', selectedValues?.value)
                            formik.setFieldValue('interval', null)
                            formik.setFieldValue('totalData', 0)
                          }}
                          className='w-100'
                          onBlur={() =>
                            formik.values.timeFrame === '' &&
                            formik.setFieldTouched('timeFrame', true)
                          }
                          styles={{
                            menu: (provided) => ({...provided, zIndex: 9999}),
                          }}
                        />
                      </div>
                      {formik.touched.timeFrame && formik.errors.timeFrame && (
                        <div className='fv-plugins-message-container text-danger'>
                          <span role='alert' className='text-danger'>
                            {formik.errors.timeFrame}
                          </span>
                        </div>
                      )}
                    </>
                    {/* realtime option */}
                    <>
                      <div
                        style={{
                          display: 'flex',
                          padding: '3px',
                          overflowWrap: 'anywhere',
                          gap: '4px',
                        }}
                        className='my-5'
                      >
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='isRealTime'
                          name='isRealTime'
                          checked={formik.values.isRealTime}
                          onChange={() => {
                            formik.setFieldValue('isRealTime', !formik.values.isRealTime)
                            formik.setFieldValue('totalData', 0)
                            formik.setFieldValue('interval', ['', ''])
                          }}
                          value={'isRealTime'}
                        />
                        <label htmlFor='relationLead' className='form-check-label me-2 ml-2'>
                          {intl.formatMessage({id: 'FORM.LABEL.REALTIME'})}
                        </label>
                      </div>
                    </>
                    {/* interval */}
                    {!formik.values.isRealTime && (
                      <>
                        <label className='form-label fs-6 fw-bolder text-dark required'>
                          {intl.formatMessage({id: 'FORM.LABEL.INTERVAL'})}
                        </label>
                        <div className='input-group mb-3'>
                          <DateRangePickerInterval
                            timeSeries={formik.values.timeFrame}
                            value={formik.values.interval}
                            onChange={handleChangeInterval}
                            container='add-component-modal'
                          />
                        </div>
                        {formik.touched.interval && formik.errors.interval && (
                          <div className='fv-plugins-message-container text-danger'>
                            <span role='alert' className='text-danger'>
                              {formik.errors.interval}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    {/* TOTAL DATA */}
                    {formik.values.isRealTime && (
                      <>
                        <label className='form-label fs-6 fw-bolder text-dark required'>
                          {intl.formatMessage({id: 'FORM.LABEL.TOTAL_DATA'})}
                        </label>
                        <div className='input-group mb-3 w-50'>
                          <input
                            placeholder={intl.formatMessage({id: 'FORM.LABEL.TOTAL_DATA'})}
                            value={formik.values.totalData}
                            onChange={(e) => {
                              const limits = {
                                DAILY: 32,
                                WEEKLY: 9,
                                MONTHLY: 25,
                              };
                              const timeFrame = formik.values.timeFrame as keyof typeof limits; 

                              if (timeFrame && limits[timeFrame] && Number(e.target.value) < limits[timeFrame]) {
                                formik.setFieldValue('totalData', customNumberFormat(Number(e.target.value)));
                              }
                            }}
                            onBlur={formik.handleBlur}
                            className={clsx('form-control form-control-lg', {
                              'is-invalid': formik.touched.totalData && formik.errors.totalData,
                            })}
                            type='text'
                            name='totalData'
                            autoComplete='off'
                            style={{zIndex: 0}}
                            disabled={!["DAILY", "WEEKLY", "MONTHLY"].includes(formik.values.timeFrame ?? "")}                         />
                          <span className='input-group-text' id='basic-addon2'>
                            {getTypeTotalData()}
                          </span>
                        </div>
                        {formik.touched.totalData && formik.errors.totalData && (
                          <div className='fv-plugins-message-container text-danger'>
                            <span role='alert' className='text-danger'>
                              {formik.errors.totalData}
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {/* button create rule/core */}
                    <>
                      <div className=' d-flex justify-content-end my-5'>
                        <button
                          type='button'
                          id='add-product-button'
                          className='btn btn-primary mt-3'
                          onClick={() => setShowAddCoreModal(true)}
                        >
                          <span className=''>
                            <i className='fas fa-plus'></i>{' '}
                            {intl.formatMessage({id: 'FORM.ACTION.ADD'})}
                            {' Core'}
                          </span>
                        </button>
                      </div>
                    </>
                    {/* table */}
                    <>
                      {formik.touched.dataCore && formik.errors.dataCore && (
                        <div className='fv-plugins-message-container text-danger'>
                          <span role='alert' className='text-danger'>
                            {formik.errors.dataCore}
                          </span>
                        </div>
                      )}
                      <TableLite columns={columns} data={formik.values.dataCore!} />
                    </>
                      <>
                        {
                          isPreviewShowed && 
                          <>
                          <div className='card border'>
                            {
                              previewComponentData && <ComponentAnalytics
                              component={previewComponentData}
                              section={section}
                              isPreview={true}
                              />
                            }
                            {
                              previewComponentLoading &&
                              <div>
                              <div className='loading-overlay'>
                                <div className='loader'></div>
                              </div>
                            </div>
                            }
                          </div>
                          </>
                        }
                    </>
                  </div>
                </div>
              </div>
              <div className='modal-footer'>
              <button
                  type='button'
                  className='btn btn-light d-none'
                  data-bs-dismiss='modal'
                  ref={closeModalRef}
                >
                  {intl.formatMessage({id: 'FORM.ACTION.CANCEL'})}
                </button>
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={handleCloseModal}
                >
                  {intl.formatMessage({id: 'FORM.ACTION.CANCEL'})}
                </button>
                <button
                  type='button'
                  id='preview-component-button'
                  className='btn text-primary btn-outline-white '
                  onClick={() => handleLoadPreview()}
                  disabled={Object.keys(formik.errors).length > 0}
                >
                  <span className=''>
                    {intl.formatMessage({id: 'FORM.ACTION.PREVIEW_COMPONENT_ANALYTIC'})}
                  </span>
                </button>
                {isPreviewShowed && (
                  <>
                    <div className='d-flex justify-content-end my-5'>
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          setIsPreviewShowed(false)
                        }}
                        className={`btn btn-danger`}
                      >
                        {intl.formatMessage({id: 'FORM.ACTION.CLOSE_PREVIEW_ANALYTIC'})}
                      </button>
                    </div>
                  </>
                )}
                <button
                  type='submit'
                  className={`btn btn-primary me-10 ${modalBtnLoading ? 'disabled' : ''}`}
                  data-kt-indicator={modalBtnLoading ? 'on' : 'off'}
                >
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'FORM.ACTION.ADD'})}
                  </span>
                  <span className='indicator-progress'>
                    Loading...{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                </button>
              </div>
            </form>
            {/* form */}
          </div>
        </div>
      </div>
      {showAddCoreModal && (
        <AddCore
          formik={formik}
          stepForm={stepForm}
          setStepForm={setStepForm}
          setShowAddCoreModal={setShowAddCoreModal}
        />
      )}
      {showEditCoreModal && (
        <EditCore
          formik={formik}
          stepForm={stepForm}
          setStepForm={setStepForm}
          setShowEditCoreModal={setShowEditCoreModal}
          index={indexEditCore!}
          setIndex={setIndexCoreModal}
          isCopyCase={isCopyCase}
          setIsCopyCase={setIsCopyCase}
        />
      )}
    </>
  )
}
